import {
    AccountCloseReasonModel,
    AccountCloseReasonResponse,
    ActivityHistoryPage,
    AdvisoryFund,
    AdvisoryFundCollection,
    AllowedInvestmentAccountTypes,
    CuratedRtqFundCollection,
    CuratedRtqInstrumentCollection,
    CuratedRtqPieChartDataItem,
    DayTradeSummaryResponse,
    DrivewealthAccount,
    DrivewealthAccountClaim,
    DrivewealthAccountCollection,
    DrivewealthAccountManagementType,
    DrivewealthAccountSummary,
    DrivewealthClosedAccountSummary,
    DrivewealthClosedAccountSummaryCollection,
    DrivewealthCreateAccountError,
    DrivewealthDayTradeCollection,
    DrivewealthDocumentListPage,
    DrivewealthHolding,
    DrivewealthManagedDepositRequest,
    DrivewealthManagedRedemptionRequest,
    DrivewealthOrderData,
    DrivewealthOrderRequest,
    DrivewealthServicerManagedRedemptionRequest,
    DrivewealthServicerOrderRequest,
    DrivewealthState,
    DrivewealthUpdateAccountRequestV2,
    DropDownModel,
    FetchState,
    FetchStateKey,
    FlatInstrument,
    FlatManagedPortfolioResponse,
    FundType,
    InstrumentDetail,
    InstrumentHolding,
    InstrumentSearchRequest,
    InstrumentSearchResponse,
    InvestmentManagementType,
    ManagedFundCollection,
    ManagedTransfersActivityHistoryPage,
    PendingActivity,
    RiskToleranceQuestionnaire
} from "@/shared/types";
import { openUrl } from "@/shared/urlHelper";
import { fetchStateKeyMatch } from "@/shared/fetchStateHelper";
import { errorIsStatus } from "@/shared/errorHelper";
import * as storeHelper from "@/shared/storeHelper";
import * as drivewealthService from "@/shared/api/drivewealthService";
import _ from "lodash";
import Vue from "vue";
import { roundCurrencyUSD } from "@/shared/currency";
import { defineStore } from "pinia";
import { useInvestmentDailyBalanceStore } from "@/shared/store/investmentDailyBalance";
import { useInvestmentThresholdStore } from "@/shared/store/investmentThreshold";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import { useDrivewealthSweepStore } from "@/shared/store/drivewealthSweep";
import { usePageSizeStore } from "@/shared/store/pageSize";
import { templatedFetchKeyStore } from "@/shared/store/fetchKeyedPlugin";
import { templatedFetchKeyCollectionStore } from "@/shared/store/fetchKeyedCollectionPlugin";
import { useUserInvestmentFeeAcknowledgementStore } from "@/employee/store/userInvestmentFeeAcknowledgement";

const pageSize = 10;

function isBeyondBounds (pageCount: number, pageIndex: number) {
    return !pageCount ||
        pageCount < indexToPageNumber(pageIndex) ||
        pageIndex < 0;
}

function pageNumberToIndex (pageNumber: number) {
    return (pageNumber - 1) * pageSize;
}

function indexToPageNumber (index: number) {
    return Math.floor(index / pageSize) + 1;
}


const toInstrumentDetail = (fund: AdvisoryFund): InstrumentDetail => !fund ? undefined : ({
    instrumentId: fund.instrumentId,
    stockSymbol: fund.stockSymbol,
    companyName: fund.company,
    status: fund.status,
    currentPrice: 0,
    fundType: getFundTypeDisplayCopy(mapFundType(fund.fundType)),
    isFundEtfOrEquity: isFundEtfOrEquity(fund.fundType),
    imageUrl: fund.imageUrl,
    website: fund.website,
    allocationPercentage: 0,
    assetCategoryName: ""
});

const holdingMapper = (holding: DrivewealthHolding): InstrumentHolding => {
    const mappedFundType = mapFundType(holding.fundType);
    return {
        stockSymbol: holding.stockSymbol,
        website: holding.webSite,
        imageUrl: holding.imageUrl,
        fundName: holding.fundName,
        fundType: mappedFundType,
        isEtfOrEquity: isFundEtfOrEquity(holding.fundType),
        shares: holding.openQuantity,
        marketPrice: holding.marketPrice,
        marketValue: holding.marketValue,
        totalReturn: holding.unrealizedProfitLoss,
        totalReturnPercentage: holding.unrealizedDayProfitLossPercent,
        costBasis: holding.costBasis,
        lastPrice: holding.priorClose,
        todaysReturn: ((holding.marketPrice - holding.priorClose) * holding.openQuantity),
        rateOfReturn: (((holding.marketValue - holding.costBasis) / holding.costBasis) * 100),
        todaysReturnPercentage: holding.unrealizedDayProfitLossPercent,
        status: holding.status
    };
};

const mapFundType = (fundType: string): FundType => {
    switch (fundType) {
        case "MUTUAL_FUND":
            return FundType.MutualFund;
        case "ETF":
            return FundType.Etf;
        case "EQUITY":
            return FundType.Equity;
        default:
            return FundType.Other;
    }
};

const fundTypePriorityOrder = (fundType: FundType) => {
    switch (fundType) {
        case FundType.MutualFund:
            return 1;
        case FundType.Etf:
            return 2;
        case FundType.Equity:
            return 3;
        case FundType.Other:
            return 4;
        default:
            return 0;
    }
};

const getFundTypeDisplayCopy = (fundType: FundType) => {
    switch (fundType) {
        case FundType.MutualFund:
            return "Mutual Fund";
        case FundType.Equity:
            return "Equity";
        case FundType.Etf:
            return "ETF";
        case FundType.Other:
            return "Other";
        default:
            return fundType;
    }
};

const isFundEtfOrEquity = (fundType: string) => {
    switch (fundType) {
        case "ETF":
        case "EQUITY":
            return true;
        default:
            return false;
    }
};

const getMappedAccountManagementType = (accountType: string) => {
    switch (accountType) {
        case InvestmentManagementType.Managed:
        case DrivewealthAccountManagementType.RiaManaged:
            return "Managed";
        case InvestmentManagementType.Advisory:
        case DrivewealthAccountManagementType.Advisory:
            return "Select";
        case InvestmentManagementType.SelfDirected:
        case DrivewealthAccountManagementType.OpenBrokerage:
            return "Choice";
        default:
            return "";
    }
};

const getMappedAccountManagementTypeDescriptionCopy = (accountType: string) => {
    switch (accountType) {
        case InvestmentManagementType.Managed:
        case DrivewealthAccountManagementType.RiaManaged:
            return "(Advisor Supported)";
        case InvestmentManagementType.Advisory:
        case DrivewealthAccountManagementType.Advisory:
            return "(Recommended Mutual Funds)";
        case InvestmentManagementType.SelfDirected:
        case DrivewealthAccountManagementType.OpenBrokerage:
            return "(Limited Brokerage)";
        default:
            return "";
    }
};

const accountTypeOrdering = {
    riaManaged: 0,
    advisory: 1,
    openBrokerage: 2
};

const getSortedDrivewealthAccounts = (accountSummaries: DrivewealthAccountSummary[]) => {
    return accountSummaries.sort((a, b) => {
        return accountTypeOrdering[a.accountManagementType] - accountTypeOrdering[b.accountManagementType];
    });
};

interface LoadAccountSummaryArgs {
    force?: boolean;
    username?: string;
    partnerId?: number;
    dwAccountId?: number;
}

export const initialState: DrivewealthState = {
    createAccountFetchState: FetchState.Uninitialized,
    closeAccountFetchState: FetchState.Uninitialized,
    liquidateAccountFetchState: FetchState.Uninitialized,
    createAccountError: null,
    activityHistory: {
        completedActivityFetchState: FetchState.Uninitialized,
        completedActivity: [],
        completedActivityFetchKey: null,
        pageNumber: 1,
        pageCount: 0
    },
    managedTransfersActivityHistory: {
        managedTransfersActivityFetchState: FetchState.Uninitialized,
        managedTransfersActivity: [],
        managedTransfersActivityFetchKey: null,
        pageNumber: 1,
        pageCount: 0
    },
    accountClaims: [],
    accountClaimsFetchState: FetchState.Uninitialized,
    accountCloseReasons: [],
    accountCloseReasonsFetchState: FetchState.Uninitialized,
    accountIdsFetchState: FetchState.Uninitialized,
    accountIds: [],
    managedPortfolio: null,
    instrumentSearch: {
        searchTerm: "",
        fetchState: FetchState.Uninitialized,
        pageNumber: 1,
        pageCount: 0,
        items: [],
    },
    enrollNowFetchState: FetchState.Uninitialized,
    accountsGetAllV2FetchState: FetchState.Uninitialized,
    allowedInvestmentAccountTypesFetchState: FetchState.Uninitialized,
    summariesGetAllV2FetchState: FetchState.Uninitialized,
    allowedInvestmentAccountTypes: null,
    userAgreementKey: null,
    userAgreementKeyFetchState: FetchState.Uninitialized,
    fundCompanies: [],
    assetClasses: [],
    showModalFlag: true,
    enrolledAccountId: null
};

// #region Pinia SubStores

const useInstrumentDetailSubStore = defineStore("drivewealth_instrumentDetail", () => {
    return templatedFetchKeyCollectionStore<InstrumentDetail, InstrumentDetail>(
        drivewealthService.getInstrumentByAccountId,
    );
});

const useAccountSummariesV2SubStore = defineStore("drivewealth_accountSummariesV2", () => {
    return templatedFetchKeyCollectionStore<DrivewealthAccountSummary, DrivewealthAccountSummary>(
        drivewealthService.getAccountSummaryV2,
    );
});

const useClosedAccountSummariesSubStore = defineStore("drivewealth_closedAccountSummaries", () => {
    return templatedFetchKeyStore<DrivewealthClosedAccountSummaryCollection, DrivewealthClosedAccountSummary[]>(
        () => ({ items: null }),
        (closedAccountsState, dto) => closedAccountsState.items = dto,
        drivewealthService.getClosedAccountSummaries,
    );
});

const useDocumentsV2SubStore = defineStore("drivewealth_documentsV2", () => {
    return templatedFetchKeyCollectionStore<DrivewealthDocumentListPage, DrivewealthDocumentListPage>(
        drivewealthService.getDocumentsV2,
    );
});

const usePendingActivityV2SubStore = defineStore("drivewealth_pendingActivityV2", () => {
    return templatedFetchKeyCollectionStore<PendingActivity, PendingActivity>(
        drivewealthService.getPendingActivityByAccount,
    );
});

const useAccountsSubStore = defineStore("drivewealth_accounts", () => {
    return templatedFetchKeyStore<DrivewealthAccountCollection, DrivewealthAccount[]>(
        () => ({ items: null }),
        (accountsState, dto) => accountsState.items = dto,
        drivewealthService.getCustomerAccounts,
    );
});

const useDayTradeSubStore = defineStore("drivewealth_daytrade", () => {
    return templatedFetchKeyStore<DrivewealthDayTradeCollection, DayTradeSummaryResponse>(
        () => ({ items: null }),
        (state, dto) => state.items = dto.trades,
        drivewealthService.getDayTrades,
    );
});

const useAdvisorySubStore = defineStore("drivewealth_advisory", () => {
    const getEmptyAdvisoryFunds = () => ({ advisoryFunds: [] });
    const advisoryFundsValueSetter = (state, dto: AdvisoryFund[]) => {
        state.advisoryFunds = dto;
    };
    return templatedFetchKeyStore<AdvisoryFundCollection, any>(
        getEmptyAdvisoryFunds,
        advisoryFundsValueSetter,
        drivewealthService.getAdvisoryFunds,
    );
});

const useCuratedRtqFundSubStore = defineStore("drivewealth_curatedRtq", () => {
    const getEmptyCuratedRtqFunds = () => ({ curatedRtqFunds: [] });
    const curatedRtqFundsValueSetter = (state, dto: AdvisoryFund[]) => {
        state.curatedRtqFunds = dto;
    };
    return templatedFetchKeyStore<CuratedRtqFundCollection, any>(
        getEmptyCuratedRtqFunds,
        curatedRtqFundsValueSetter,
        drivewealthService.getCuratedRtqFunds,
    );
});

const useCuratedRtqInstrumentsSubStore = defineStore("drivewealth_curatedrtq_instruments", () => {
    const getCuratedRtqInstruments = () => ({ curatedRtqInstruments: [] });
    const curatedRtqInstrumentsValueSetter = (state, dto: InstrumentDetail[]) => {
        state.curatedRtqInstruments = dto;
    };
    return templatedFetchKeyStore<CuratedRtqInstrumentCollection, any>(
        getCuratedRtqInstruments,
        curatedRtqInstrumentsValueSetter,
        drivewealthService.getCuratedRtqInstrumentList,
    );
});

const useManagedFundSubStore = defineStore("drivewealth_managedPortfolio", () => {
    const getEmptyManagedFunds = () => ({ managedFunds: [] });
    const managedFundsValueSetter = (state, dto: FlatManagedPortfolioResponse) => {
        state.managedFunds = dto.instruments;
    };
    return templatedFetchKeyStore<ManagedFundCollection, any>(
        getEmptyManagedFunds,
        managedFundsValueSetter,
        drivewealthService.getManagedPortfolio,
    );
});

// #endregion

export const useDrivewealthStore = defineStore("drivewealth", {
    state: () => (_.cloneDeep(initialState)),
    getters: {
        pendingActivityV2: () => usePendingActivityV2SubStore(),
        accounts: () => useAccountsSubStore(),
        dayTrade: () => useDayTradeSubStore(),
        advisory: () => useAdvisorySubStore(),
        curatedRtqSubStore: () => useCuratedRtqFundSubStore(),
        curatedRtq: () => useCuratedRtqInstrumentsSubStore(),
        managedFundSubStore: () => useManagedFundSubStore(),
        accountSummariesV2Store: () => useAccountSummariesV2SubStore(),
        closedAccountSummariesSubStore: () => useClosedAccountSummariesSubStore(),
        documentsV2: () => useDocumentsV2SubStore(),
        advisoryFundsLoading (): boolean {
            return this.advisory.fetchState === FetchState.Loading;
        },
        advisoryFundsLoaded (): boolean {
            return this.advisory.fetchState === FetchState.Loaded;
        },
        advisoryFunds (): AdvisoryFund[] {
            return this.advisory.advisoryFunds;
        },
        closedAccountsLoaded (): boolean {
            return this.closedAccountSummariesSubStore.fetchState === FetchState.Loaded;
        },
        closedAccounts (): DrivewealthClosedAccountSummary[] {
            return this.closedAccountSummariesSubStore.items;
        },
        curatedRtqFundsLoaded (): boolean {
            return this.curatedRtqSubStore.fetchState === FetchState.Loaded;
        },
        summariesGetAllV2Loaded (): boolean {
            return this.summariesGetAllV2FetchState === FetchState.Loaded;
        },
        curatedRtqFunds (): AdvisoryFund[] {
            return this.curatedRtqSubStore.curatedRtqFunds;
        },
        managedFundsLoaded (): boolean {
            return this.managedFundSubStore.fetchState === FetchState.Loaded;
        },
        managedFunds (): FlatInstrument[] {
            return this.managedFundSubStore.managedFunds;
        },
        advisoryFundBySymbol () {
            return symbol => {
                return this.advisory.advisoryFunds
                    .find(f => f.stockSymbol === symbol);
            };
        },
        curatedRtqInstrumentsLoaded (): boolean {
            return this.curatedRtq.fetchState === FetchState.Loaded;
        },
        curatedRtqInstruments (): InstrumentDetail[] {
            return this.curatedRtq.curatedRtqInstruments;
        },
        curatedRtqPieChartDataItems (): CuratedRtqPieChartDataItem[] {
            if (!this.curatedRtq.curatedRtqInstruments && !this.advisory.advisoryFunds) {
                return [];
            }

            let mappedPieChartDataItems: CuratedRtqPieChartDataItem[] = [];

            if (this.curatedRtq.curatedRtqInstruments.length) {
                mappedPieChartDataItems = this.curatedRtq.curatedRtqInstruments.map(f => ({
                    assetCategoryName: f.assetCategoryName,
                    allocationPercentage: f.allocationPercentage,
                }));
            } else {
                mappedPieChartDataItems = this.advisory.advisoryFunds.map(f => ({
                    assetCategoryName: f.assetCategoryName,
                    allocationPercentage: f.allocationPercentage,
                }));
            }

            return _.uniqBy(mappedPieChartDataItems, "assetCategoryName");
        },
        accountIdsLoaded (): boolean {
            return this.accountIdsFetchState === FetchState.Loaded;
        },
        hasAccountIdOrIds (): boolean {
            return this.accountIds.length > 0;
        },
        holdingBySymbol () {
            return (symbol, accountId) => {
                const accountSummaryV2 = this.accountSummaryById(accountId);

                if (accountSummaryV2) {
                    const holding = accountSummaryV2.holdings.find(h => h.stockSymbol === symbol);
                    return holding;
                }
            };
        },
        holdingsPageCount () {
            return accountId => {
                const pageSizeStore = usePageSizeStore();
                const accountSummaryV2 = this.accountSummaryById(accountId);

                if (accountSummaryV2) {
                    const holdings = _.orderBy(accountSummaryV2.holdings, x => fundTypePriorityOrder(x.fundType))
                        .map(holdingMapper);
                    return Math.ceil(holdings.length / pageSizeStore.pageSize);
                }

                return 0;
            };
        },
        holdingsForPage () {
            return (accountId, pageNumber) => {
                const accountSummaryV2 = this.accountSummaryById(accountId);
                const pageIndex = pageNumber - 1;
                const pageSizeStore = usePageSizeStore();

                if (accountSummaryV2) {
                    const holdings = accountSummaryV2.holdings.map(holdingMapper);
                    const orderedHoldings = _.orderBy(holdings, x => fundTypePriorityOrder(x.fundType));

                    if (isBeyondBounds(this.holdingsPageCount(accountId), pageIndex)) {
                        return [];
                    }
                    const numberOfRecordsToSkip = pageSizeStore.pageSize * pageIndex;
                    return orderedHoldings.slice(numberOfRecordsToSkip, (numberOfRecordsToSkip + pageSizeStore.pageSize));
                }

                return [];
            };
        },
        isAccountClaimsLoaded () {
            return this.accountClaimsFetchState === FetchState.Loaded;
        },
        createAccountLoading: state => state.createAccountFetchState === FetchState.Loading,
        closeAccountLoading: state => state.closeAccountFetchState === FetchState.Loading,
        closeAccountLoaded: state => state.closeAccountFetchState === FetchState.Loaded,
        isEnrollingNow: state => state.enrollNowFetchState === FetchState.Loading,
        hasAnAccount (): boolean {
            return this.hasAccountIdOrIds;
        },
        isAccountManagementType () {
            return (managementType: string, accountId?: number) => {
                if (accountId) {
                    const accountSummary: DrivewealthAccountSummary = this.accountSummaryById(accountId);

                    if (accountSummary && accountSummary.accountManagementType ===
                        managementType) {
                        return true;
                    }
                }

                return false;
            };
        },
        accountManagementType () {
            return (accountId?: number) => {
                if (accountId) {
                    const accountSummary = this.accountSummaryById(accountId);

                    if (accountSummary) {
                        return accountSummary.accountManagementType;
                    }
                }

                return "";
            };
        },
        accountManagementTypeTitle () {
            return (accountId?: number) => {
                if (accountId) {
                    const accountSummary = this.accountSummaryById(accountId);

                    if (accountSummary) {
                        return `${getMappedAccountManagementType(accountSummary.accountManagementType)}`;
                    }
                }

                return "";
            };
        },
        accountManagementTypeDescriptionCopy () {
            return (accountId?: number) => {
                if (accountId) {
                    const accountSummary = this.accountSummaryById(accountId);

                    if (accountSummary) {
                        return `${getMappedAccountManagementTypeDescriptionCopy(accountSummary.accountManagementType)}`;
                    }
                }

                return "";
            };
        },
        fundTypeDisplayCopy () {
            return (fundType?: string) => {

                return getFundTypeDisplayCopy(mapFundType(fundType));
            };
        },
        fundTypeDisplayCopyV2 () {
            return (fundType: FundType) => {

                return getFundTypeDisplayCopy(fundType);
            };
        },
        isFundEtfOrEquity () {
            return (fundType?: string) => {

                return isFundEtfOrEquity(fundType);
            };
        },
        hasDocuments () {
            return this.documentsV2.items !== undefined;
        },
        completedActivityLoading: state => state.activityHistory.completedActivityFetchState === FetchState.Loading,
        completedActivityLoaded: state => state.activityHistory.completedActivityFetchState === FetchState.Loaded,
        managedTransfersActivityLoading: state =>
            state.managedTransfersActivityHistory.managedTransfersActivityFetchState === FetchState.Loading,
        managedTransfersActivityLoaded: state =>
            state.managedTransfersActivityHistory.managedTransfersActivityFetchState === FetchState.Loaded,
        pendingActivityById () {
            return dwAccountId => {
                return this.pendingActivityV2.items[dwAccountId];
            };
        },
        pendingActivityV2Loaded () {
            return dwAccountId => {
                const pendingActivityFetchState = this.pendingActivityV2.fetchStates[`${dwAccountId}`];

                if (pendingActivityFetchState) {
                    return pendingActivityFetchState === FetchState.Loaded;
                }

                return false;
            };
        },
        pendingActivityV2Loading () {
            return dwAccountId => {
                const pendingActivityFetchState = this.pendingActivityV2.fetchStates[`${dwAccountId}`];

                if (pendingActivityFetchState) {
                    return pendingActivityFetchState === FetchState.Loading;
                }

                return false;
            };
        },
        activityPageIndex: state => pageNumberToIndex(state.activityHistory.pageNumber),
        activityPageTotal: state => state.activityHistory.pageCount,
        completedActivity () {
            const pageIndex = this.activityPageIndex;
            if (isBeyondBounds(this.activityHistory.pageCount, pageIndex)) {
                return [];
            }

            return this.activityHistory.completedActivity;
        },
        managedTransfersActivityPageIndex: state => pageNumberToIndex(state.managedTransfersActivityHistory.pageNumber),
        managedTransfersActivityPageTotal: state => state.managedTransfersActivityHistory.pageCount,
        managedTransfersActivity () {
            const pageIndex = this.managedTransfersActivityPageIndex;
            if (isBeyondBounds(this.managedTransfersActivityHistory.pageCount, pageIndex)) {
                return [];
            }

            return this.managedTransfersActivityHistory.managedTransfersActivity;
        },
        instrumentDetails () {
            return useInstrumentDetailSubStore().items;
        },
        instrumentDetailBySymbol () {
            return symbol => {
                return useInstrumentDetailSubStore().items[symbol];
            };
        },
        cachedInstrumentDetailBySymbol () {
            return symbol => {
                return this.instrumentDetailBySymbol(symbol) ||
                    toInstrumentDetail(this.advisoryFundBySymbol(symbol));
            };
        },
        instrumentHeading () {
            return (symbol, accountId) => {
                if (this.isAccountManagementType(DrivewealthAccountManagementType.RiaManaged, accountId)) {
                    const accountSummary = this.accountSummaryById(accountId);
                    if (accountSummary) {
                        return `${accountSummary.portfolioClientId} - ${accountSummary.portfolioName}`;
                    }
                }

                return symbol;
            };
        },
        instrumentSubheading () {
            return (symbol, accountId) => {
                if (this.isAccountManagementType(DrivewealthAccountManagementType.RiaManaged, accountId)) {
                    const accountSummary = this.accountSummaryById(accountId);
                    if (accountSummary) {
                        return accountSummary.description;
                    }
                }

                return this.cachedInstrumentDetailBySymbol(symbol).companyName;
            };
        },
        instrumentDataIsLoaded () {
            return symbol => {
                if (this.isAccountManagementType("RIA_MANAGED")) {
                    return true;
                }

                return this.advisoryFundsLoaded &&
                    !!this.cachedInstrumentDetailBySymbol(symbol);
            };
        },
        amountHeld () {
            return (symbol, accountId) => {
                if (this.isAccountManagementType(DrivewealthAccountManagementType.RiaManaged, accountId)) {
                    const accountSummary = this.accountSummaryById(accountId);
                    if (accountSummary) {
                        return accountSummary.equityValue;
                    }
                }

                const holding = this.holdingBySymbol(symbol, accountId);
                if (!holding) {
                    return 0;
                }
                return roundCurrencyUSD(holding.availableForTradingQty * holding.marketPrice);
            };
        },
        cashAvailableForWithdrawal () {
            return (accountId?: number) => {
                const accountSummary = this.accountSummaryById(accountId);

                if (accountSummary &&
                    accountSummary.accountManagementType === DrivewealthAccountManagementType.RiaManaged) {
                    return accountSummary.cashAvailableForWithdrawal;
                }

                return 0;
            };
        },
        availableForTradingQty () {
            return (symbol, accountId) => {
                const holding = this.holdingBySymbol(symbol, accountId);
                return holding ? holding.availableForTradingQty : 0;
            };
        },
        hsaInvestmentThreshold () {
            const investmentThresholdStore = useInvestmentThresholdStore();
            if (!investmentThresholdStore.thresholdsLoaded) {
                return 0;
            }

            return investmentThresholdStore.investmentThreshold(this.hasAnAccount);
        },
        isHsaBalanceAndThresholdLoaded () {
            return (accountId?: number) => {
                const investmentDailyBalanceStore = useInvestmentDailyBalanceStore();
                if (accountId) {
                    return investmentDailyBalanceStore.isHsaBalanceAndThresholdLoaded &&
                        this.pendingActivityV2Loaded(accountId);
                }
                return investmentDailyBalanceStore.isHsaBalanceAndThresholdLoaded;
            };

        },
        hsaBalanceBeyondThreshold () {
            return (accountId?: number) => {
                if (!this.isHsaBalanceAndThresholdLoaded(accountId)) {
                    return 0;
                }
                const hsaDailyBalanceStore = useHsaDailyBalanceStore();
                const hsaBalance = hsaDailyBalanceStore.getInstantBalance.amount ||
                    hsaDailyBalanceStore.balanceOnFile.amount;
                const holdAmount = this.pendingActivityById(accountId).holdAmount || 0;
                return Math.max(0, hsaBalance - this.hsaInvestmentThreshold - holdAmount);
            };
        },
        instrumentSearchTerm: state => state.instrumentSearch.searchTerm,
        instrumentPageIndex: state => pageNumberToIndex(state.instrumentSearch.pageNumber),
        instrumentPageTotal: state => state.instrumentSearch.pageCount,
        instrumentSearchResults: state => state.instrumentSearch.items,
        instrumentSearchIsLoading: state => state.instrumentSearch.fetchState === FetchState.Loading,
        instrumentSearchIsLoaded: state => state.instrumentSearch.fetchState === FetchState.Loaded,
        getAccountManagementTypeTitle: state => (managementType: string) =>
            managementType ? `${getMappedAccountManagementType(managementType)}` : "",
        getAccountManagementTypeDescriptionCopy: state => (managementType: string) =>
            managementType ? `${getMappedAccountManagementTypeDescriptionCopy(managementType)}` : "",
        getMappedAccountManagementType: state => (managementType: string) =>
            managementType ? getMappedAccountManagementType(managementType) : "",
        accountsLoading: () => useAccountsSubStore().fetchState === FetchState.Loading,
        accountsLoaded: () => useAccountsSubStore().fetchState === FetchState.Loaded,
        accountSummariesV2Loaded: state => state.summariesGetAllV2FetchState === FetchState.Loaded,
        accountSummaryById (): (dwAccountID: any) => DrivewealthAccountSummary {
            return dwAccountId => {
                return this.accountSummariesV2Store.items[dwAccountId];
            };
        },
        accountSummariesV2 () {
            if (this.accountSummariesV2Loaded) {
                return Object.values(this.accountSummariesV2Store.items).flat() as DrivewealthAccountSummary[];
            }

            return [];
        },
        accountSummariesSortedByDrivewealthAccountType (): DrivewealthAccountSummary[] {
            const sortedAccountSummaries = getSortedDrivewealthAccounts(this.accountSummariesV2);
            let claimedAccountSummaries = [];

            sortedAccountSummaries.forEach(account => {
                const accountClaim = this.getAccountClaimByAccountId(account.drivewealthAccountId);

                if (!accountClaim || (accountClaim && accountClaim.isClaimed)) {
                    claimedAccountSummaries = [...claimedAccountSummaries, account];
                }
            });

            return claimedAccountSummaries;
        },
        accountSummaryV2Loaded () {
            return dwAccountId => {
                const summaryFetchState = this.accountSummariesV2Store.fetchStates[`${dwAccountId}`];

                if (summaryFetchState) {
                    return summaryFetchState === FetchState.Loaded;
                }

                return false;
            };
        },
        documentsById () {
            return (dwAccountId, pageNumber) => {
                return this.documentsV2.items[`${dwAccountId}_${pageNumber}`];
            };
        },
        documentsV2Loaded () {
            return (dwAccountId, pageNumber) => {
                const documentListPage = this.documentsV2.fetchStates[`${dwAccountId}_${pageNumber}`];

                if (documentListPage) {
                    return documentListPage === FetchState.Loaded;
                }

                return false;
            };
        },
        pagedDocumentsById () {
            return (dwAccountId, pageNumber) => {
                const documentListPage = this.documentsV2.items[`${dwAccountId}_${pageNumber}`];

                if (documentListPage) {
                    return documentListPage.records;
                }

                return null;
            };
        },
        documentsV2PageTotal () {
            return (dwAccountId, pageNumber) => {
                const documentListPage = this.documentsV2.items[`${dwAccountId}_${pageNumber}`];

                if (documentListPage) {
                    return documentListPage.numberOfPages;
                }

                return 1;
            };
        },
        isAllowedInvestmentAccountTypesLoaded: state => state.allowedInvestmentAccountTypesFetchState ===
            FetchState.Loaded,
        isInvestmentEnrollmentDisabled: state => state.allowedInvestmentAccountTypes
            && !state.allowedInvestmentAccountTypes.hasAdvisory
            && !state.allowedInvestmentAccountTypes.hasRiaManaged
            && !state.allowedInvestmentAccountTypes.hasSelfDirected,
        isCuratedRtqEnabled: state => state.allowedInvestmentAccountTypes
            && state.allowedInvestmentAccountTypes.isCuratedRtqEnabled,
        showInvestments () {
            return this.hasAnAccount || !this.isInvestmentEnrollmentDisabled;
        },
        drivewealthAllAccountBalance () {
            const summaries = this.accountSummariesV2;
            if (summaries) {
                return _.sumBy(summaries, "displayBalance");
            }
            return 0;
        },
        canOpenAnotherInvestmentAccount () {
            let canOpenAnotherAccount = false;

            const advisoryAccount = _.find(this.accountSummariesV2Store.items,
                x => x.accountManagementType === DrivewealthAccountManagementType.Advisory);
            if (this.allowedInvestmentAccountTypes.hasAdvisory && advisoryAccount === undefined) {
                canOpenAnotherAccount = true;
            }

            const selfAccount = _.find(this.accountSummariesV2Store.items,
                x => x.accountManagementType === DrivewealthAccountManagementType.OpenBrokerage);
            if (this.allowedInvestmentAccountTypes.hasSelfDirected && selfAccount === undefined) {
                canOpenAnotherAccount = true;
            }

            const managedAccount = _.find(this.accountSummariesV2Store.items,
                x => x.accountManagementType === DrivewealthAccountManagementType.RiaManaged);
            if (this.allowedInvestmentAccountTypes.hasRiaManaged && managedAccount === undefined) {
                canOpenAnotherAccount = true;
            }

            return canOpenAnotherAccount;
        },
        getUserAgreementDocumentKey () {
            return this.userAgreementKey;
        },
        isUserAgreementDocumentKeyLoaded () {
            return this.userAgreementKeyFetchState === FetchState.Loaded;
        },
        showModal () {
            return this.showModalFlag;
        }
    },
    actions: {
        setEnrollNowFetchState (fetchState: FetchState) {
            this.enrollNowFetchState = fetchState;
        },
        setCreateAccountFetchState (fetchState: FetchState) {
            this.createAccountFetchState = fetchState;
        },
        setUpdateAccountFetchState (fetchState: FetchState) {
            this.createAccountFetchState = fetchState;
        },
        setCloseAccountFetchState (fetchState: FetchState) {
            this.closeAccountFetchState = fetchState;
        },
        setLiquidateAccountFetchState (fetchState: FetchState) {
            this.liquidateAccountFetchState = fetchState;
        },
        setAccountCloseReasonsData (accountCloseReasons: AccountCloseReasonResponse[]) {
            this.accountCloseReasons = [ ...accountCloseReasons ];
        },
        setAccountCloseReasonsFetchState (fetchState: FetchState) {
            this.accountCloseReasonsFetchState = fetchState;
        },
        setCreateAccountError (error: DrivewealthCreateAccountError) {
            this.createAccountError = error;
        },
        updateAccountClaim (accountClaim: DrivewealthAccountClaim) {
            const indexToUpdate = this.accountClaims.findIndex(
                x => x.drivewealthApiAccountId === accountClaim.drivewealthApiAccountId);
            this.accountClaims[indexToUpdate].isClaimed = accountClaim.isClaimed;
        },
        invalidateCompletedActivityFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.activityHistory.completedActivityFetchKey)) {
                return;
            }
            this.activityHistory.completedActivityFetchKey = null;
            this.activityHistory.completedActivity = [];
            this.activityHistory.completedActivityFetchState = FetchState.Uninitialized;
        },
        setCompletedActivityFetchState ({ fetchState, key }: {
            fetchState: FetchState,
            key: FetchStateKey
        }) {
            this.activityHistory.completedActivityFetchState = fetchState;
            this.activityHistory.completedActivityFetchKey = key;
        },
        setCompletedActivity (mutationArguments:
                                  {
                                      key: FetchStateKey,
                                      data: ActivityHistoryPage
                                  }) {
            if (!mutationArguments.data) {
                return;
            }
            this.activityHistory.completedActivityFetchKey = mutationArguments.key;
            this.activityHistory.pageNumber = mutationArguments.data.page;
            this.activityHistory.pageCount = mutationArguments.data.numberOfPages;
            this.activityHistory.completedActivity = mutationArguments.data.records;
        },
        setCompletedActivityPageNumber (pageNumber: number) {
            if (isBeyondBounds(this.activityHistory.pageCount, pageNumberToIndex(pageNumber))) {
                return;
            }

            if (this.activityHistory.pageNumber !== pageNumber) {
                this.activityHistory.completedActivityFetchState = FetchState.Uninitialized;
            }

            this.activityHistory.pageNumber = pageNumber;
        },
        invalidateManagedTransfersActivityFetchState (key: FetchStateKey) {
            if (fetchStateKeyMatch(key, this.managedTransfersActivityHistory.managedTransfersActivityFetchKey)) {
                return;
            }
            this.managedTransfersActivityHistory.managedTransfersActivityFetchKey = null;
            this.managedTransfersActivityHistory.managedTransfersActivity = [];
            this.managedTransfersActivityHistory.managedTransfersActivityFetchState = FetchState.Uninitialized;
        },
        setManagedTransfersActivityFetchState (
            { fetchState, key }: {
                fetchState: FetchState,
                key: FetchStateKey
            }) {
            this.managedTransfersActivityHistory.managedTransfersActivityFetchState = fetchState;
            this.managedTransfersActivityHistory.managedTransfersActivityFetchKey = key;
        },
        setManagedTransfersActivity (mutationArguments:
                                         {
                                             key: FetchStateKey,
                                             data: ManagedTransfersActivityHistoryPage
                                         }) {
            if (!mutationArguments.data || !mutationArguments.data.records
                || !mutationArguments.data.records.length) {
                return;
            }
            this.managedTransfersActivityHistory.managedTransfersActivityFetchKey = mutationArguments.key;
            this.managedTransfersActivityHistory.pageNumber = mutationArguments.data.page;
            this.managedTransfersActivityHistory.pageCount = mutationArguments.data.numberOfPages;
            this.managedTransfersActivityHistory.managedTransfersActivity = mutationArguments.data.records;
        },
        setManagedTransfersActivityPageNumber (pageNumber: number) {
            if (isBeyondBounds(this.managedTransfersActivityHistory.pageCount, pageNumberToIndex(pageNumber))) {
                return;
            }

            if (this.managedTransfersActivityHistory.pageNumber !== pageNumber) {
                this.managedTransfersActivityHistory.managedTransfersActivityFetchState = FetchState.Uninitialized;
            }

            this.managedTransfersActivityHistory.pageNumber = pageNumber;
        },
        setSummariesGetAllV2FetchState (fetchState: FetchState) {
            this.summariesGetAllV2FetchState = fetchState;
        },
        setAccountSummariesV2FetchState (
            { fetchState, dwAccountId }: {
                fetchState: FetchState,
                dwAccountId: number
            }) {
            Vue.set(this.accountSummariesV2Store.fetchStates, dwAccountId, fetchState);
        },
        setDocumentsV2FetchState ({ fetchState, itemKey }:
                                      {
                                          fetchState: FetchState,
                                          itemKey: string,
                                          pageNumber: number
                                      }) {
            Vue.set(this.documentsV2.fetchStates, itemKey, fetchState);
        },
        setManagedPortfolio (portfolio: FlatManagedPortfolioResponse) {
            this.managedPortfolio = portfolio;
        },
        startInstrumentSearch (searchTerm: string, pageSize: number, pageNumber: number) {
            this.instrumentSearch.fetchState = FetchState.Loading;
            this.instrumentSearch.pageSize = pageSize;
            this.instrumentSearch.pageNumber = pageNumber;
            this.instrumentSearch.searchTerm = searchTerm;
        },
        setInstrumentSearchResults (results: InstrumentSearchResponse) {
            this.instrumentSearch.fetchState = FetchState.Loaded;
            this.instrumentSearch.pageCount = results.pageCount;
            this.instrumentSearch.pageNumber = results.pageNumber;
            this.instrumentSearch.items = results.instruments;
        },
        instrumentSearchFailed () {
            this.instrumentSearch.fetchState = FetchState.Uninitialized;
        },
        resetInstrumentSearch () {
            this.instrumentSearch.fetchState = FetchState.Uninitialized;
            this.instrumentSearch.searchTerm = "";
            this.instrumentSearch.pageNumber = 1;
            this.instrumentSearch.pageCount = 0;
            this.instrumentSearch.items = [];
        },
        setAllowedInvestmentAccountTypes (allowedTypes: AllowedInvestmentAccountTypes) {
            this.allowedInvestmentAccountTypes = allowedTypes;
        },
        setAccountIds (accountIds: number[]) {
            this.accountIds = [ ...accountIds ];
        },
        setAccountIdsFetchState (fetchState: FetchState) {
            this.accountIdsFetchState = fetchState;
        },
        setAllowedInvestmentAccountTypesFetchState (fetchState: FetchState) {
            this.allowedInvestmentAccountTypesFetchState = fetchState;
        },
        setAccountClaimsFetchState (fetchState: FetchState) {
            this.accountClaimsFetchState = fetchState;
        },
        setAccountClaimsData (accountClaims: DrivewealthAccountClaim[]) {
            this.accountClaims = accountClaims;
        },
        setFundCompanies (fundCompanies: DropDownModel[]) {
            this.fundCompanies = fundCompanies;
        },
        setAssetClasses (assetClasses: DropDownModel[]) {
            this.assetClasses = assetClasses;
        },
        setShowModal (showModal: boolean) {
            this.showModalFlag = showModal;
        },
        setEnrolledAccountId (accountId: number) {
            this.enrolledAccountId = accountId;
        },
        loadAdvisoryFunds (
            options: {
                username?: string,
                partnerId?: number,
                force?: boolean
            }): Promise<any> {
            const subStore = useAdvisorySubStore();

            return subStore.fetchKeyedApiCall(options, null, options.force)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadCuratedRtqFunds (
            options: {
                username?: string,
                partnerId?: number,
                force?: boolean
            }): Promise<any> {
            const subStore = useCuratedRtqFundSubStore();

            return subStore.fetchKeyedApiCall(options, null, options.force)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadManagedFunds (
            options: {
                username?: string,
                partnerId?: number,
                force?: boolean
            }): Promise<any> {
            const subStore = useManagedFundSubStore();

            return subStore.fetchKeyedApiCall(options, null, options.force)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadCuratedRtqInstruments (
            options: {
                username?: string,
                partnerId?: number,
                instrumentListId: number,
                force?: boolean
            }): Promise<any> {
            const subStore = useCuratedRtqInstrumentsSubStore();

            return subStore.fetchKeyedApiCall(
                { username: options.username, partnerId: options.partnerId },
                `${options.instrumentListId}`,
                options.force
            )
            .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        loadDocumentInTab (
            options: {
                documentId: number,
                username?: string,
                partnerId?: number
            }) {
            return drivewealthService.getDocumentUrl(options.documentId, options.username, options.partnerId)
                .then(response => {
                    const url = response.data;
                    openUrl(url);
                })
                .catch(err => {
                  storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        loadPendingActivityByAccount ({ username, partnerId, accountId, force }: {
            username?: string,
            partnerId?: number,
            force?: boolean,
            accountId?: number
        }): Promise<any> {
            if (!force && (this.pendingActivityV2.fetchStates[accountId] === FetchState.Loading ||
                this.pendingActivityV2.fetchStates[accountId] === FetchState.Loaded)) {
                return Promise.resolve();
            }

            return usePendingActivityV2SubStore().fetchKeyedApiCall(
                { username, partnerId },
                `${accountId}`,
                `${accountId}`,
                force
            ).catch(err => {
                return storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        async loadCompletedActivityByAccount (options:
                                                  {
                                                      username?: string,
                                                      partnerId?: number,
                                                      accountId?: number,
                                                      force?: boolean
                                                  }): Promise<any> {
            options = options || {};
            const fetchStateKey: FetchStateKey = {
                username: options.username,
                partnerId: options.partnerId,
                accountId: options.accountId
            };
            this.invalidateCompletedActivityFetchState(fetchStateKey);
            if (!options.force && this.activityHistory.completedActivityFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setCompletedActivityFetchState({ fetchState: FetchState.Loading, key: fetchStateKey });
            return drivewealthService.getCompletedActivityByAccount(
                    this.activityHistory.pageNumber, pageSize, options.username, options.partnerId, options.accountId)
                .then(result => {
                    this.setCompletedActivityFetchState({ fetchState: FetchState.Loaded, key: fetchStateKey });
                    if (result.data) {
                        const data = result.data;
                        this.setCompletedActivity({ key: fetchStateKey, data });
                    }
                })
                .catch(err => {
                    if (errorIsStatus(err, 404)) {
                        this.setCompletedActivityFetchState({ fetchState: FetchState.Loaded, key: fetchStateKey });
                    } else {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    }
                });
        },
        async loadManagedTransfersActivityByAccount (options:
                                                         {
                                                             username?: string,
                                                             partnerId?: number,
                                                             accountId?: number
                                                         }): Promise<any> {
            options = options || {};
            const fetchStateKey: FetchStateKey = {
                username: options.username,
                partnerId: options.partnerId,
                accountId: options.accountId
            };
            if (this.managedTransfersActivityHistory.managedTransfersActivityFetchState === FetchState.Loading) {
                return Promise.resolve();
            }
            this.invalidateManagedTransfersActivityFetchState(fetchStateKey);
            this.setManagedTransfersActivityFetchState({ fetchState: FetchState.Loading, key: fetchStateKey });
            return drivewealthService.getManagedTransfersActivityByAccount(
                    this.managedTransfersActivityHistory.pageNumber, pageSize, options.username, options.partnerId,
                    options.accountId)
                .then(result => {
                    this.setManagedTransfersActivityFetchState(
                        { fetchState: FetchState.Loaded, key: fetchStateKey });
                    if (result.data) {
                        const data = result.data;
                        this.setManagedTransfersActivity({ key: fetchStateKey, data });
                    }
                })
                .catch(err => {
                    if (errorIsStatus(err, 404)) {
                        this.setManagedTransfersActivityFetchState(
                            { fetchState: FetchState.Loaded, key: fetchStateKey });
                    } else {
                        return storeHelper.handleError({ error: err, rethrow: true });
                    }
                });
        },
        loadAccountClaims (options: { username?: string, partnerId?: number }): Promise<any> {
            if (this.accountClaimsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            return drivewealthService.getDrivewealthAccountClaims(options.username, options.partnerId)
            .then(result => {
                if (result.data) {
                    this.setAccountClaimsData(result.data);
                }
            })
            .catch(err => {
                if (!errorIsStatus(err, 404)) {
                    storeHelper.handleError({ error: err, rethrow: true });
                }
            })
            .finally(() => {
                this.setAccountClaimsFetchState(FetchState.Loaded);
            });
        },
        getAccountClaimByAccountId (accountId: number): any {
            return this.accountClaims.find(c => c.drivewealthApiAccountId === accountId);
        },
        async loadAccountIds (options: { force?: boolean} ): Promise<any> {
            if (!options.force && this.accountIdsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setAccountIdsFetchState(FetchState.Loading);

            return drivewealthService.getAccountIdsV2()
                .then(result => {
                    this.setAccountIdsFetchState(FetchState.Loaded);
                    if (result.data) {
                        this.setAccountIds(result.data);
                    }
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async loadAccounts (options: {
            username?: string,
            partnerId?: number
        }): Promise<any> {
            return useAccountsSubStore().fetchKeyedApiCall(options)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async loadClosedAccounts (options: {
            username?: string,
            partnerId?: number
        }): Promise<any> {
            const closedAccountsSubStore = useClosedAccountSummariesSubStore();
            return closedAccountsSubStore.fetchKeyedApiCall(options)
            .catch(err => {
                storeHelper.handleError({ error: err, rethrow: false });
            });
        },
        async closeAccount (accountId: number, reason: AccountCloseReasonModel): Promise<any> {
            const drivewealthSweepStore = useDrivewealthSweepStore();

            this.setCloseAccountFetchState(FetchState.Loading);
            return drivewealthService.closeAccount(accountId, reason)
                .then(result => {
                    const account = result.data;
                    this.setCloseAccountFetchState(FetchState.Loaded);
                    // reload the account ids
                    this.loadAccountIds({ force: true });
                    // reload the auto-sweep settings
                    drivewealthSweepStore.getSweepV2({ force: true });
                })
                .catch(err => {
                    this.setCloseAccountFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async getAccountCloseReasons (): Promise<any> {
            if (this.accountCloseReasonsFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setAccountCloseReasonsFetchState(FetchState.Loading);
            return drivewealthService.getAccountCloseReasons()
                .then(result => {
                    this.setAccountCloseReasonsFetchState(FetchState.Loaded);
                    if (result.data) {
                        this.setAccountCloseReasonsData(result.data);
                    }
                })
                .catch(err => {
                    this.setAccountCloseReasonsFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async liquidateAccount (accountId: number, partnerId: number): Promise<any> {
            this.setLiquidateAccountFetchState(FetchState.Loading);
            return drivewealthService.liquidateAccount(accountId, partnerId)
                .then(_ => {
                    this.setLiquidateAccountFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setLiquidateAccountFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        updateAccountV2 (request: DrivewealthUpdateAccountRequestV2): Promise<any> {
            this.setUpdateAccountFetchState(FetchState.Loading);
            return drivewealthService.updateAccountV2(request.accountId, request)
                .then(result => {
                    const account = result.data;
                    this.setUpdateAccountFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setUpdateAccountFetchState(FetchState.Uninitialized);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });

        },
        async createCancelOrderRequest (bendOrderId: string): Promise<any> {
            return drivewealthService.postCancelOrderRequest(bendOrderId)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async createOrderRequestByAccount (
            options: {
                data: DrivewealthOrderData,
                accountId: number
            }): Promise<any> {

            const priceOnScreen = this.cachedInstrumentDetailBySymbol(options.data.stockSymbol).currentPrice;
            const request: DrivewealthOrderRequest = {
                amount: options.data.amount,
                stockSymbol: options.data.stockSymbol,
                side: options.data.side,
                units: options.data.units,
                priceOnScreen
            };

            return drivewealthService.postOrderRequestByAccount(request, options.accountId)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async servicerCreateOrderRequestByAccount (
            options: {
                username: string,
                partnerId: number,
                data: DrivewealthOrderData,
                accountId: number
            }): Promise<any> {

            const priceOnScreen = this.cachedInstrumentDetailBySymbol(options.data.stockSymbol).currentPrice;
            const request: DrivewealthServicerOrderRequest = {
                amount: options.data.amount,
                stockSymbol: options.data.stockSymbol,
                side: options.data.side,
                units: options.data.units,
                priceOnScreen,
                forUser: options.username
            };

            return drivewealthService.servicerPostOrderRequestByAccount(options.partnerId, request, options.accountId)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async createManagedDepositByAccount (
            options: {
                amount: number,
                accountId: number
            }): Promise<any> {
            const request: DrivewealthManagedDepositRequest = {
                amount: options.amount
            };

            return drivewealthService.postManagedDepositByAccount(request, options.accountId)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async createManagedRedemptionByAccount (
            options: {
                data: DrivewealthManagedRedemptionRequest,
                accountId: number
            }): Promise<any> {
            return drivewealthService.postManagedRedemptionByAccount(options.data, options.accountId)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async servicerCreateManagedRedemptionByAccount (
            options: {
                partnerId: number,
                data: DrivewealthServicerManagedRedemptionRequest,
                accountId: number
            }): Promise<any> {
            return drivewealthService.servicerPostManagedRedemptionByAccount(options.partnerId, options.data, options.accountId)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async loadDayTrades (options: {
            username?: string,
            partnerId?: number
        }): Promise<any> {
            return useDayTradeSubStore().fetchKeyedApiCall(options)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async evaluateQuestionnaire (answers: RiskToleranceQuestionnaire): Promise<any> {
            return drivewealthService.postRiskToleranceQuestionnaire(answers)
                .then(response => {
                    this.setManagedPortfolio(response.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async storeQuestionnaire (answers: RiskToleranceQuestionnaire): Promise<any> {
            return drivewealthService.putRiskToleranceQuestionnaire(answers)
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async enrollNow (): Promise<any> {
            if (this.enrollNowFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            const userInvestmentFeeAcknowledgementStore = useUserInvestmentFeeAcknowledgementStore();
            this.setEnrollNowFetchState(FetchState.Loading);
            return drivewealthService.postEnrollNow()
                .then(result => {
                    this.setEnrolledAccountId(result.data.drivewealthAccountId);
                    this.setCreateAccountFetchState(FetchState.Loaded);
                    this.setEnrollNowFetchState(FetchState.Loaded);
                    if (result.data.accountManagementType === DrivewealthAccountManagementType.OpenBrokerage) {
                        userInvestmentFeeAcknowledgementStore.setResponseData({signedChoiceFee: true});
                    }
                })
                .catch(err => {
                    this.setEnrollNowFetchState(FetchState.Loaded);
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async getInstrumentDetails (
            { stockSymbol, username, partnerId, accountId }:
                {
                    stockSymbol: string,
                    username?: string,
                    partnerId?: number,
                    accountId?: number
                }
        ): Promise<any> {
            if ((this.instrumentDetail && this.instrumentDetail.fetchStates[stockSymbol] === FetchState.Loading)
                || stockSymbol === "RIAMANAGED") {
                return Promise.resolve();
            }

            return useInstrumentDetailSubStore().fetchKeyedApiCall(
                    { username, partnerId },
                    stockSymbol,
                    { stockSymbol, accountId }
                )
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        async openInstrumentDetailFundResearchUrl (
            args: {
                stockSymbol: string,
                fallbackUrl: string
            }): Promise<string> {
            return drivewealthService.getInstrumentFundResearchUrl(args.stockSymbol)
                .then(response => {
                    const fundReasearchUrl = response.data || args.fallbackUrl;
                    openUrl(fundReasearchUrl);
                })
                .catch(err => {
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        loadAccountSummariesV2 (options: {
            force?: boolean,
            username?: string,
            partnerId?: number
        }): Promise<any> {
            if (!options.force && this.summariesGetAllV2FetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setSummariesGetAllV2FetchState(FetchState.Loading);
            return drivewealthService.getAccountSummariesV2(options.username, options.partnerId)
                .then(result => {
                    const accounts = result.data;
                    this.setSummariesGetAllV2FetchState(FetchState.Loaded);

                    // update new accountsV2 with data
                    useAccountSummariesV2SubStore().resetState();
                    _.forEach(accounts, account => {
                        useAccountSummariesV2SubStore().setValueLoaded({
                            fetchKey: null,
                            itemKey: account.drivewealthAccountId,
                            value: account
                        });
                    });
                })
                .catch(err => {
                    if (errorIsStatus(err, 404)) {
                        this.setSummariesGetAllV2FetchState(FetchState.Loaded);
                    } else {
                        storeHelper.handleError({ error: err, rethrow: true });
                    }
                });
        },

        async getAccountSummaryV2 (
            args: LoadAccountSummaryArgs = {}
        ): Promise<any> {
            const { dwAccountId, partnerId, username, force } = args;

            if (!force) {
                if (this.accountSummariesV2Store.fetchStates[dwAccountId] === FetchState.Loading ||
                    this.accountSummariesV2Store.fetchStates[dwAccountId] === FetchState.Loaded) {
                    return Promise.resolve();
                }
            }

            return useAccountSummariesV2SubStore().fetchKeyedApiCall(
                    { username, partnerId },
                    `${dwAccountId}`,
                    `${dwAccountId}`
                )
                .catch(err => {
                    if (errorIsStatus(err, 404)) {
                        this.setAccountSummariesV2FetchState({ dwAccountId, fetchState: FetchState.Uninitialized });
                        return Promise.resolve();
                    } else {
                        this.setAccountSummariesV2FetchState({ dwAccountId, fetchState: FetchState.Uninitialized });
                        return storeHelper.handleError({ error: err, rethrow: true });
                    }
                });
        },

        async getDocumentsV2 (
            args: {
                username?: string,
                partnerId?: number,
                dwAccountId: number,
                pageNumber: number
            }
        ): Promise<any> {
            const itemKey = `${args.dwAccountId}_${args.pageNumber}`;
            if (this.documentsV2.fetchStates[itemKey] === FetchState.Loading ||
                this.documentsV2.fetchStates[itemKey] === FetchState.Loaded) {
                return Promise.resolve();
            }

            return useDocumentsV2SubStore().fetchKeyedApiCall(
                    { username: args.username, partnerId: args.partnerId },
                    itemKey,
                    { dwAccountId: args.dwAccountId, page: args.pageNumber, pageSize }
                )
                .catch(err => {
                    this.setDocumentsV2FetchState({
                        itemKey, fetchState: FetchState.Uninitialized
                    });
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },

        async loadHsaBalanceAndThreshold ({ force, username, partnerId, accountId }: {
            force?: boolean,
            username?: string,
            partnerId?: number,
            accountId?: number
        }) {
            const investmentDailyBalanceStore = useInvestmentDailyBalanceStore();
            if (accountId) {
                return Promise.all([
                    this.loadPendingActivityByAccount({ username, partnerId, force, accountId }),
                    investmentDailyBalanceStore.loadHsaBalanceAndThreshold({ force, username, partnerId }) ]
                ).catch(err => storeHelper.handleError({ error: err, rethrow: false }));
            } else {
                return Promise.all([
                    investmentDailyBalanceStore.loadHsaBalanceAndThreshold({ force, username, partnerId }) ]
                ).catch(err => storeHelper.handleError({ error: err, rethrow: false }));
            }
        },

        async loadInstrumentData (symbol: string, accountId: number, options: { username?: string, partnerId?: number }) {
            if (!this.isAccountManagementType("RIA_MANAGED")) {
                return Promise.all([
                    this.loadAdvisoryFunds({ username: options.username, partnerId: options.partnerId }),
                    this.getInstrumentDetails({
                        stockSymbol: symbol, checkCache: true, accountId,
                        username: options.username, partnerId: options.partnerId
                    }),
                ]);
            }
        },

        searchInstruments ({ searchTermParam, accountId, pageNumber, pageSize, availableToBuyOnly }:
                               { searchTermParam: string, accountId: number, pageNumber: number, pageSize: number, availableToBuyOnly?: boolean }) {
            const searchTerm = searchTermParam.trim();
            if (this.instrumentSearch.searchTerm === searchTerm && this.instrumentSearch.pageNumber === pageNumber &&
                this.instrumentSearch.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }

            if (!searchTerm) {
                this.resetInstrumentSearch();
                return Promise.resolve();
            }

            this.startInstrumentSearch(searchTerm, pageSize, pageNumber);
            return drivewealthService.searchInstrumentsByAccountId(searchTerm, accountId, pageNumber, pageSize, availableToBuyOnly)
                .then(response => {
                    if (this.instrumentSearchTerm !== searchTerm) {
                        return Promise.resolve();
                    }
                    this.setInstrumentSearchResults(response.data);
                })
                .catch(err => {
                    this.instrumentSearchFailed();
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        searchInstrumentsWithFundType (searchRequest: InstrumentSearchRequest) {
            const searchTerm = searchRequest.searchTerm.trim();

            if (!searchTerm && searchRequest.fundTypeBit === "000" && !searchRequest.fundCompanyId &&
                !searchRequest.assetClassId && !searchRequest.indexFundOnly) {
                this.resetInstrumentSearch();
                return Promise.resolve();
            }

            this.startInstrumentSearch(searchTerm, searchRequest.pageSize, searchRequest.pageNumber);
            return drivewealthService.searchInstrumentsWithFundType(searchRequest)
                .then(response => {
                    if (this.instrumentSearchTerm !== searchTerm || response.data === null) {
                        this.resetInstrumentSearch();
                        return Promise.resolve();
                    }
                    this.setInstrumentSearchResults(response.data);
                })
                .catch(err => {
                    this.instrumentSearchFailed();
                    return storeHelper.handleError({ error: err, rethrow: true });
                });
        },
        async loadFundCompanies () {
            return drivewealthService.getFundCompanies()
                .then(r => {
                    this.setFundCompanies(r.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async loadAssetClasses () {
            return drivewealthService.getAssetClasses()
                .then(r => {
                    this.setAssetClasses(r.data);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async loadAllowedInvestmentAccountTypes () {
            if (this.allowedInvestmentAccountTypesFetchState === FetchState.Loading) return Promise.resolve();
            return drivewealthService.getEmployeeInvestmentTypes()
                .then(r => {
                    this.setAllowedInvestmentAccountTypes(r.data);
                    this.setAllowedInvestmentAccountTypesFetchState(FetchState.Loaded);
                })
                .catch(err => storeHelper.handleError({ error: err, rethrow: true }));
        },
        async loadUserAgreementDocumentKey (accountType: string) {
            if (this.userAgreementKeyFetchState !== FetchState.Uninitialized) {
                return Promise.resolve(this.userAgreementKey);
            }
            this.setUserAgreementDocumentKeyFetchState(FetchState.Loading);

            return drivewealthService.getUserAgreementDocumentKey(accountType).then(resp => {
                this.setUserAgreementDocumentKey(resp.data);
                this.setUserAgreementDocumentKeyFetchState(FetchState.Loaded);
            }).catch(err => {
                storeHelper.handleError({ error: err, rethrow: true });
            });
        },
        setUserAgreementDocumentKey (key: string) {
            this.userAgreementKey = key;
        },
        setUserAgreementDocumentKeyFetchState (fetchState: FetchState) {
            this.userAgreementKeyFetchState = fetchState;
        },
        resetUserAgreementDocumentKey () {
            this.userAgreementKeyFetchState = FetchState.Uninitialized;
            this.userAgreementKey = null;
        }
    }
});
